import { Avatar, Group, Loader, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ActivityEventDto, TaskActivityResponse } from '@ledokol/contracts'
import { fetchTaskActivity } from '../../shared/api/activity'

export function TaskHistory({ taskId }: { taskId: string }) {
  const q = useQuery({
    queryKey: ['task', taskId, 'activity'],
    queryFn: () => fetchTaskActivity(taskId),
  })

  if (q.isLoading) return <Loader size="sm" />
  if (!q.data) return <Text size="sm" c="red">Не удалось загрузить историю</Text>

  const lines = q.data.events
    .map((ev) => ({ ev, text: renderLine(ev, q.data) }))
    .filter((x): x is { ev: ActivityEventDto; text: string } => x.text !== null)

  if (lines.length === 0) {
    return <Text size="xs" c="dimmed">Событий пока нет</Text>
  }

  return (
    <Stack gap="xs">
      {lines.map(({ ev, text }) => (
        <Group key={ev.id} gap={6} wrap="nowrap" align="flex-start">
          <Avatar size={22} radius="xl" color={ev.actor ? 'blue' : 'gray'}>
            {ev.actor ? ev.actor.full_name[0] : '⚙'}
          </Avatar>
          <Stack gap={0} style={{ flex: 1 }}>
            <Group gap={6}>
              <Text size="sm" fw={600}>
                {ev.actor ? ev.actor.full_name : 'Система'}
              </Text>
              <Text size="xs" c="dimmed">
                {dayjs(ev.created_at).format('D MMM YYYY HH:mm')}
              </Text>
            </Group>
            <Text size="sm">{text}</Text>
          </Stack>
        </Group>
      ))}
    </Stack>
  )
}

const PRIORITY_RU: Record<string, string> = {
  low: 'Низкий',
  med: 'Средний',
  high: 'Высокий',
  urgent: 'Срочный',
}

function val(v: unknown, key: string): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) return (v as Record<string, unknown>)[key]
  return undefined
}

function str(v: unknown, key: string): string | null {
  const x = val(v, key)
  return typeof x === 'string' ? x : null
}

/**
 * Строка ленты для события; null — событие в истории не показываем
 * (started/completed/reopened — KPI-дубли: им всегда сопутствует status_changed).
 */
function renderLine(ev: ActivityEventDto, data: TaskActivityResponse): string | null {
  const userName = (id: unknown): string => {
    if (typeof id !== 'string' || !id) return '—'
    return data.users.find((u) => u.id === id)?.full_name ?? '—'
  }
  const due = (v: unknown): string => {
    const s = str(v, 'due_at')
    return s ? dayjs(s).format('D MMM YYYY HH:mm') : '—'
  }

  switch (ev.event_type) {
    case 'task_created':
      return 'Задача создана'
    case 'title_changed':
      return `Название: «${str(ev.from_value, 'title') ?? '—'}» → «${str(ev.to_value, 'title') ?? '—'}»`
    case 'description_changed':
      return 'Описание изменено'
    case 'status_changed':
      return `Статус: ${str(ev.from_value, 'name') ?? '—'} → ${str(ev.to_value, 'name') ?? '—'}`
    case 'assignee_changed':
      return `Исполнитель: ${userName(val(ev.from_value, 'user_id'))} → ${userName(val(ev.to_value, 'user_id'))}`
    case 'due_changed':
      return `Срок: ${due(ev.from_value)} → ${due(ev.to_value)}`
    case 'priority_changed': {
      const f = str(ev.from_value, 'priority')
      const t = str(ev.to_value, 'priority')
      return `Приоритет: ${(f && PRIORITY_RU[f]) ?? f ?? '—'} → ${(t && PRIORITY_RU[t]) ?? t ?? '—'}`
    }
    case 'comment_added':
      return 'Комментарий добавлен'
    case 'comment_edited':
      return 'Комментарий отредактирован'
    case 'comment_deleted':
      return 'Комментарий удалён'
    case 'tag_added':
      return `Тег добавлен: ${str(ev.to_value, 'name') ?? ''}`
    case 'tag_removed':
      return `Тег снят: ${str(ev.from_value, 'name') ?? ''}`
    case 'watcher_added':
      return `Наблюдатель добавлен: ${userName(val(ev.to_value, 'user_id'))}`
    case 'watcher_removed':
      return `Наблюдатель убран: ${userName(val(ev.from_value, 'user_id'))}`
    case 'closed':
      // ВАЖНО: closed = задача удалена (soft-delete), а НЕ «выполнена»
      return 'Задача удалена'
    case 'tg_draft_created':
      return 'Черновик создан из Telegram'
    case 'tg_draft_confirmed':
      return 'Черновик из Telegram подтверждён'
    case 'tg_draft_rejected':
      return 'Черновик из Telegram отклонён'
    case 'checklist_item_added':
      return 'Пункт чек-листа добавлен'
    case 'checklist_item_done':
      return 'Пункт чек-листа выполнен'
    case 'checklist_item_undone':
      return 'Пункт чек-листа снят'
    case 'checklist_item_removed':
      return 'Пункт чек-листа удалён'
    case 'attachment_added': {
      const n = str(ev.payload, 'file_name')
      return n ? `Файл прикреплён: «${n}»` : 'Файл прикреплён'
    }
    case 'attachment_removed': {
      const n = str(ev.payload, 'file_name')
      return n ? `Файл удалён: «${n}»` : 'Файл удалён'
    }
    // KPI-события, всегда дублирующие status_changed — в ленте не показываем
    case 'started':
    case 'completed':
    case 'reopened':
      return null
    default:
      return ev.event_type
  }
}
